import { AttendanceValidation, Geofence, PointValidation } from '../types/geofenceTypes';
import apiClient from './apiClient';
import { saveResponseAsFile } from '../utils/downloadFile';

/**
 * Carga (o reemplaza) la geocerca de un proyecto subiendo un archivo .kmz.
 *
 * @param file         Archivo .kmz.
 * @param projectId    Identificador del proyecto.
 * @param bufferMeters Ancho del corredor en metros (opcional).
 */
export const uploadGeofence = async (
    file: File,
    projectId: number,
    bufferMeters?: number
): Promise<Geofence> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/Geofences/upload/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: bufferMeters ? { bufferMeters } : {},
    });
    return response.data;
};

/**
 * Obtiene la geocerca de un proyecto (para previsualizarla en el mapa).
 * Devuelve null si el proyecto aún no tiene geocerca definida.
 */
export const getGeofenceByProject = async (projectId: number): Promise<Geofence | null> => {
    try {
        const response = await apiClient.get(`/Geofences/${projectId}`);
        return response.data;
    } catch (error: any) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

/**
 * Valida si una coordenada (latitud/longitud) cae dentro del rango del proyecto.
 */
export const validatePoint = async (
    projectId: number,
    latitude: number,
    longitude: number
): Promise<PointValidation> => {
    const response = await apiClient.post(`/Geofences/validate/${projectId}`, {
        latitude,
        longitude,
    });
    return response.data;
};

/**
 * Descarga la geocerca del proyecto como archivo .kmz.
 *
 * El backend reconstruye el archivo a partir de la geometria guardada: el .kmz original no se
 * almacena, asi que lo que se descarga es la geometria que el sistema esta usando.
 */
export const downloadGeofenceKmz = async (projectId: number): Promise<void> => {
    const response = await apiClient.get<Blob>(`/Geofences/${projectId}/kmz`, { responseType: 'blob' });
    saveResponseAsFile(response, 'Geocerca.kmz');
};

/** Elimina la geocerca de un proyecto. */
export const deleteGeofence = async (projectId: number): Promise<void> => {
    await apiClient.delete(`/Geofences/${projectId}`);
};

/**
 * Consulta en vivo los registros de asistencia geolocalizados de BUK y asigna a cada uno el proyecto/centro
 * de coste correcto según la geocerca que contiene su coordenada. El obra_id por defecto es 30833 (backend).
 */
export const resolveAttendanceGeo = async (
    from: string,
    to: string,
    obraId?: number
): Promise<AttendanceValidation[]> => {
    const response = await apiClient.get('/Geofences/resolve-attendance', {
        params: { from, to, ...(obraId ? { obraId } : {}) },
    });
    return response.data;
};
